// src/library/chat/chat-items.tsx
import React, { useMemo } from "react";
import {
  Alert,
  Button,
  Card,
  Flex,
  Heading,
  Text,
  View,
  useTheme,
} from "@aws-amplify/ui-react";
import { ConversationEvent } from "@heartcheck/shared";
import { TypingEffect } from "../typeEffect";

interface ChatItemProps {
  text: string;
  event: ConversationEvent & { disableTyping?: boolean };
  lastEventTime: number;
}

interface ActionResult {
  url?: string;
  label?: string;
}

// Shared text style to wrap long content safely
const wrapTextStyle: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

// Small SSR-safe helper for opening links in a new tab
const canUseWindow = typeof window !== "undefined";

function openInNewTab(url?: string) {
  if (!url) return;
  if (!canUseWindow) return;
  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    // best-effort only; ignore failures
  }
}

// Phrases indicating automated medical decision support we need to suppress
const BAN_PATTERNS: RegExp[] = [
  /\b(fr(amingham|amingham)|ascvd|score2|qrisk|pce)\b/i,
  /\b(risk\s*(estimate|score|level|category)?\s*[:=]\s*(high|medium|moderate|low))\b/i,
  /\bclassify(ing)?\s*risk\b/i,
  /\b(diagnos(e|is|tic)|triage|clinical decision|decision support)\b/i,
  /\b(treat(ment)?|prescrib(e|ing|ed)|medication plan|dose|dosing)\b/i,
  /\b(start|stop|increase|decrease)\s+(the\s+)?(dose|medication|drug)\b/i,
  /\bcontraindicat(ed|ion)s?\b/i,
  /\bclearance\s*to\s*(exercise|operate)\b/i,
  /\b(percent|percentage)\b\s*\d{1,3}\s*%/i,
];

// Two disclaimers to remove defensively on the client as well
const DISCLAIMER_REGEXES: RegExp[] = [
  /(?:^|\n)\s*—?\s*Educational\s+use\s+only\.[\s\S]{0,500}?call\s+112\s*\(EU\)\.(?:\s*|\n)/gi,
  /(?:^|\n)\s*If\s+these\s+concerns\s+apply\s+to\s+you[\s\S]{0,500}?call\s+112\s*\(EU\)\s*now\.(?:\s*|\n)/gi,
];

// Replace banned content with a neutral placeholder
function stripBannedContent(text: string): string {
  let out = text || "";
  BAN_PATTERNS.forEach((re) => {
    out = out.replace(re, "[automated medical decision content removed]");
  });
  // Simple percentage scrub (e.g., 10% risk) — conservative
  out = out.replace(/\b\d{1,3}\s?%\b/g, "[percentage removed]");
  return out;
}

function stripTwoDisclaimers(text: string): string {
  let out = text || "";
  DISCLAIMER_REGEXES.forEach((rx) => {
    out = out.replace(rx, "");
  });
  // collapse excessive blank lines
  return out.replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Public helper to sanitize agent output before rendering.
 * IMPORTANT: does NOT append any education footer or emergency CTA.
 */
export function sanitizeAgentText(raw: string): string {
  const base = typeof raw === "string" ? raw : String(raw ?? "");
  const stripped = stripBannedContent(base);
  const noDisclaimers = stripTwoDisclaimers(stripped);
  return noDisclaimers;
}

const UserChatMessageBase = (props: ChatItemProps) => {
  const actionResult =
    (props.event.event.actionResult as unknown as ActionResult | undefined) ||
    undefined;

  return (
    <View textAlign="right" paddingLeft={20} paddingRight={20}>
      <Card lineHeight={2}>
        <Text style={wrapTextStyle}>{props.text}</Text>

        {props.event.event.actionRequested === "Payment" && actionResult && (
          <Button
            variation="primary"
            size="small"
            onClick={() => openInNewTab(actionResult.url)}
          >
            {actionResult.label ? actionResult.label : "Proceed to Payment"}
          </Button>
        )}
      </Card>
    </View>
  );
};

export const UserChatMessage = React.memo(
  UserChatMessageBase,
  (a, b) =>
    a.event.id === b.event.id &&
    a.text === b.text &&
    a.lastEventTime === b.lastEventTime
);

const UserChatErrorBase = (props: ChatItemProps) => {
  return (
    <View paddingLeft={20} paddingRight={20}>
      <Card lineHeight={2}>
        <Alert variation="error" fontSize="smaller">
          {props.text}
        </Alert>
      </Card>
    </View>
  );
};

export const UserChatError = React.memo(UserChatErrorBase);

const AgentChatMessageBase = (props: ChatItemProps) => {
  // Sanitize only; DO NOT append footer or show 112 CTA
  const processedText = useMemo(
    () => sanitizeAgentText(props.text),
    [props.text]
  );

  return (
    <View textAlign="left" paddingLeft={20} paddingRight={20}>
      <View lineHeight={2}>
        <Text style={wrapTextStyle}>
          {props.event.disableTyping && processedText}
          {!props.event.disableTyping && (
            // span is safe here; TypingEffect does the animation
            <span style={wrapTextStyle as any}>
              <TypingEffect startTime={props.lastEventTime} text={processedText} />
            </span>
          )}
        </Text>
      </View>
    </View>
  );
};

export const AgentChatMessage = React.memo(
  AgentChatMessageBase,
  (a, b) =>
    a.event.id === b.event.id &&
    a.text === b.text &&
    a.lastEventTime === b.lastEventTime &&
    !!a.event.disableTyping === !!b.event.disableTyping
);

const AgentPartialChatMessageBase = (props: { text: string }) => {
  const processedText = useMemo(
    () => sanitizeAgentText(props.text),
    [props.text]
  );
  return (
    <View textAlign="left" paddingLeft={20} paddingRight={20}>
      <View lineHeight={2}>
        <Text style={wrapTextStyle}>{processedText}</Text>
      </View>
    </View>
  );
};

export const AgentPartialChatMessage = React.memo(AgentPartialChatMessageBase);

function tryFixJsonString(render: string | undefined | null): string {
  let out = typeof render === "string" ? render : String(render ?? "");

  // strip outer quotes and unescape \n
  if (out.startsWith('"') && out.endsWith('"')) {
    out = out.substring(1, out.length - 1).replace(/\\n/g, "\n");
  }
  if (out.startsWith("json")) {
    out = out.substring(4);
  }

  const parseJsonSafely = (s: string) => {
    try {
      return JSON.parse(s);
    } catch {
      /* try single quote -> double */
    }
    try {
      return JSON.parse(s.replace(/'/g, '"'));
    } catch {
      return s;
    }
  };

  const parsed = parseJsonSafely(out);
  return typeof parsed === "string" ? parsed : JSON.stringify(parsed, null, 2);
}

const AgentJSONBlockBase = (props: ChatItemProps) => {
  const raw = tryFixJsonString(props.text);
  const processedText = useMemo(
    () => sanitizeAgentText(String(raw)),
    [raw]
  );

  return (
    <View textAlign="left" paddingLeft={20} paddingRight={20}>
      <View lineHeight={2}>
        <Text>
          <pre>
            <code>{processedText}</code>
          </pre>
        </Text>
      </View>
    </View>
  );
};

export const AgentJSONBlock = React.memo(
  AgentJSONBlockBase,
  (a, b) => a.event.id === b.event.id && a.text === b.text
);

const AgentGraphQLBlockBase = (
  props: { invoke: () => void } & ChatItemProps
) => {
  // Do not sanitize GraphQL query text itself; it's a dev utility.
  return (
    <View textAlign="left" paddingLeft={20} paddingRight={20}>
      <View lineHeight={2}>
        <Card paddingLeft={10} className="codeBoxHeader">
          <Flex direction="row" justifyContent="space-between">
            <Heading>GraphQL Query</Heading>
            <Heading>
              <Button className="invokeButton" onClick={props.invoke}>
                Click To Invoke
              </Button>
            </Heading>
          </Flex>
        </Card>
        <pre>
          <code>
            <Text>{props.text}</Text>
          </code>
        </pre>
      </View>
    </View>
  );
};

export const AgentGraphQLBlock = React.memo(AgentGraphQLBlockBase);

const GraphQLResultBlockBase = (props: ChatItemProps) => {
  const raw = tryFixJsonString(props.text);
  const processedText = useMemo(
    () => sanitizeAgentText(String(raw)),
    [raw]
  );

  return (
    <View textAlign="left" paddingLeft={20} paddingRight={20}>
      <View lineHeight={2}>
        <Card paddingLeft={10} className="codeBoxHeader">
          <Heading>Query Result</Heading>
        </Card>
        <pre>
          <code>
            <Text>{processedText}</Text>
          </code>
        </pre>
      </View>
    </View>
  );
};

export const GraphQLResultBlock = React.memo(
  GraphQLResultBlockBase,
  (a, b) => a.event.id === b.event.id && a.text === b.text
);

const AgentInnerDialogBlockBase = (props: ChatItemProps) => {
  const theme = useTheme();
  const processedText = useMemo(
    () => sanitizeAgentText(props.text),
    [props.text]
  );

  return (
    <View textAlign="left" paddingLeft={20} paddingRight={20}>
      <View lineHeight={2} padding={theme.tokens.space.medium}>
        <Text style={wrapTextStyle}>
          . . .
          <TypingEffect startTime={props.lastEventTime} text={processedText} />
        </Text>
      </View>
    </View>
  );
};

export const AgentInnerDialogBlock = React.memo(AgentInnerDialogBlockBase);

const AgentWarningBlockBase = (props: ChatItemProps) => {
  const processedText = useMemo(
    () => sanitizeAgentText(props.text),
    [props.text]
  );

  return (
    <View textAlign="left" paddingLeft={20} paddingRight={20}>
      <View lineHeight={2}>
        <Alert variation="warning" fontSize="smaller">
          <TypingEffect startTime={props.lastEventTime} text={processedText} />
        </Alert>
      </View>
    </View>
  );
};
export const AgentWarningBlock = React.memo(AgentWarningBlockBase);
